/**
 * Constants-based group configuration for sections that use the group-aware renderer.
 *
 * Sections listed here opt into the new 12-column responsive grid with Fraunces H2
 * group headers. Sections NOT listed fall through to the existing flat card renderer —
 * no behavioral change for those sections.
 *
 * Established session 47 as the About You rebuild reference pattern. Add other sections
 * here incrementally as they're rebuilt.
 *
 * Related: section-affordances.ts (per-section affordance overrides)
 */

import { SECTION_SLUGS } from './sections';
import { FIELD_KEYS } from './fields';

export type FieldGroup = {
  id: string;
  title: string;
  subtitle?: string;
  fieldKeys: string[];
  // s109 (AY-1 Build 1): when true, the collapsible group renders collapsed on
  // first visit. Omitted/false → expanded by default. Declarative so Build 5
  // (per-group completion) can flip default state per group via config.
  defaultCollapsed?: boolean;
};

export type SectionGroupConfig = {
  groups: FieldGroup[];
  ungroupedBehavior: 'append' | 'hide';
};

// s154 round 2 (item 3): active-but-non-input About You fields — kept isActive:true (the gov-form
// field_maps + the planner lens read them) but NEVER rendered as a card input. full_legal_name is
// DERIVED from the name parts on save (FIELDS-NAME-1) and has no user-facing input; without this it
// leaks into the "ungrouped" card fallback (the s154 round-2 bare-value bug). The card renderer and
// the "every active field is grouped" guard both treat these as exempt (grouped ∨ card-hidden).
export const CARD_HIDDEN_FIELD_KEYS: Set<string> = new Set(['full_legal_name']);

// Field keys that render as SIF clay cards.
// Keyed by field_key (not field_type) so the routing is explicit and DB type is irrelevant.
// s111 Build 6: emptied — phone_unlock + primary_email_password moved to the Protect
// Your Access section, whose card routes Cat A via isCategoryAField (no SIF-group config).
export const SIF_ROUTED_FIELD_KEYS: Set<string> = new Set([]);

// ✅ s175 (SPOUSE-GATE-SUBSTRATE-1) — `SPOUSE_CONDITIONAL_FIELD_KEYS` and `MARITAL_STATUS_SHOW_SPOUSE`
// are DELETED. They were a SECOND gate substrate: the four spouse fields (`spouse_name`,
// `spouse_phone`, `spouse_dob`, `spouse_sin`) carried `conditions: null` and were hidden by a
// hardcoded Set that the card renderer AND the planner lens each had to remember to consult. That is
// why LENS-GATES-1 (s175) had to read two substrates at once, and — one step earlier — why the lens
// could not simply evaluate `conditions` and had a married planner asking for a cohabitation date.
//
// The gate now lives where every other gate lives: a spec `conditions` block on each of the four
// fields (`shared/template-spec/about-you.ts`), `showIf marital_status in
// "Married|Common-Law Partner|Separated|Widowed"`, evaluated by the ONE shared `evaluateShowIf`.
//
// 🔴 THE 4-VALUE VISIBILITY GATE STILL DIFFERS FROM THE 3-VALUE FILL GATE, DELIBERATELY. `SPOUSE_GATE`
// (server/form-field-maps.ts) excludes **Widowed**: a planner may record a late spouse's details, but
// a widowed *deceased* has no surviving spouse to name on a CPP survivor claim, so filling would
// print a predeceased spouse on a federal form as if alive. **Do NOT reconcile the two.** The
// divergence is now guarded behaviourally in `server/__tests__/spouse-gate-divergence.test.ts`,
// which asserts the two gates differ on exactly one value and that the value is Widowed.
//
// 🔴 One consequence worth knowing (D-127, GATE-CLEAR-1): because the gate is a `conditions` block
// now, closing it CLEARS the spouse values (blank-not-delete, planner sees a 409 confirm naming
// them). That is the design doing its job — it stops a prior marriage's spouse printing on an
// ISP-1200/1300 — and Widowed sitting INSIDE the visibility set is what keeps a late spouse's
// record safe from it.

// Placeholder text for fields, keyed by field_key.
// Rendered as the placeholder attribute on the <input>; not stored in DB.
export const FIELD_PLACEHOLDERS: Record<string, string> = {
  first_name:       'e.g. John',
  middle_name:      'e.g. Anthony',
  family_name:      'e.g. Smith',
  name_at_birth:    'e.g. a maiden name, if different',
  // s224 (chapter-one walk, item D) — was 'e.g. Toronto, Ontario', which invited a city AND a
  // province into a field whose label asks for a city, and whose province and country already
  // have their own fields below. Measured on prod: 4 of 5 stored values were city-only and one
  // read "Toronto, ON, Canada" — planner-typed, on the one surface that asked for it. There is
  // no Places lookup on this field anywhere; the two surfaces simply disagreed. Matches the
  // interview board's own copy (shared/interview/board-template.ts: "e.g. Brantford").
  place_of_birth:   'e.g. Brantford',
  country_of_birth: 'e.g. Canada',
  citizenship:      'e.g. Canadian, Dual Canadian/British',
  spouse_name:      'e.g. Jane Marie Smith',
  spouse_phone:     'e.g. (416) 555-1234',
  last_occupation:  'e.g. Software Engineer, Retired Teacher',
};

// ---------------------------------------------------------------------------
// Col-span lookup — MUST use literal class strings so Tailwind JIT includes them.
// Dynamic template strings (e.g. `md:col-span-${n}`) are purged at build time.
// ---------------------------------------------------------------------------

export const COL_SPAN_CLASSES: Record<number, string> = {
  3:  'col-span-12 md:col-span-3',
  4:  'col-span-12 md:col-span-4',
  6:  'col-span-12 md:col-span-6',
  12: 'col-span-12',
};

// Per-field col-span overrides. Takes precedence over field-type defaults.
export const FIELD_COL_SPAN_OVERRIDES: Record<string, number> = {
  first_name:               4, // s135: first / middle / family sit three-across on one row
  middle_name:              4,
  family_name:              4,
  name_at_birth:            6,
  also_known_as:            6,
  date_of_birth:            4,
  place_of_birth:           4,
  country_of_birth:         4, // s147: place_of_birth / country_of_birth / citizenship = one 12-col row
  province_of_birth:        4, // s147 Phase 1b: conditional (Canada) — sits in the birth row
  citizenship:              4,
  marital_status:           6,
  father_name:              6,
  mother_maiden_name:       6,
  spouse_name:              6,
  spouse_phone:             6, // s147: name(6) + phone(6) = one row; dob(3) + marriage(3) below
  spouse_dob:               3,
  marriage_date:            3,
  cohabitation_start_date:  3, // FIELD-AUDIT-1 (D-154.12)
  separation_date:          3, // FIELD-AUDIT-1 (D-154.12)
  spouse_sin:               6, // FIELD-AUDIT-1 (D-154.5)
  organ_donor_status:      12, // s154 round 2 (item 3, A1): its own full-width row after Citizenship
  home_address:            12,
  last_occupation:          6,
  // s109 (AY-1 Build 2): the About You toggles render full-width so each toggle +
  // its CaptureUpstreamRenderer mini-list is a self-contained, vertically-stacked
  // block (a "Yes" list no longer shoves an adjacent quarter-width field). Without
  // these, yes_no falls to the type default (3 / quarter width).
  hasChildren:             12,
  hasPets:                 12,
  owns_business:           12,
  owns_property:           12,
  hasFirearms:             12, // s119 capture-only toggle — full-width block (matches sibling toggles)
  hasVeteransService:      12, // s171 R-A capture-only toggle — same full-width block
  // s111 Build 5: work_situation select renders full-width so its capture-upstream
  // mini-list (Business / Employment) reads as a child block, like the toggles above.
  work_situation:          12,
};

// Field-type defaults (fallback when no per-field override exists).
export function getDefaultColSpanForFieldType(fieldType: string): number {
  switch (fieldType) {
    case 'date':     return 4;
    case 'yes_no':   return 3;
    case 'select':   return 6;
    case 'address':  return 12;
    default:         return 12;
  }
}

// Returns the number of columns for a field (override → type default → 12).
export function getColSpanForField(fieldKey: string | null | undefined, fieldType: string): number {
  if (fieldKey && FIELD_COL_SPAN_OVERRIDES[fieldKey] !== undefined) {
    return FIELD_COL_SPAN_OVERRIDES[fieldKey];
  }
  return getDefaultColSpanForFieldType(fieldType);
}

// Returns the literal Tailwind class string for a field's col-span.
export function getColSpanClass(fieldKey: string | null | undefined, fieldType: string): string {
  const n = getColSpanForField(fieldKey, fieldType);
  return COL_SPAN_CLASSES[n] ?? 'col-span-12';
}

// Legacy — kept for fieldWidthToColSpan in the existing flat renderer.
export function getDefaultColSpan(fieldType: string): number {
  return getDefaultColSpanForFieldType(fieldType);
}

// s109 (AY-1 Build 1) → s111 Build 6: About You now has 3 collapsible groups
// (Identity / Family / Home and Work). The former interim 4th "Secure Information"
// group was REMOVED in Build 6 — its 5 fields relocated to the new "Protect Your
// Access" category. The card renders fields in THIS array order, NOT by
// generic_fields.sort_order. All groups default collapsed (s109 Build 3, Fix 1):
// About You lands with just the group headers; the planner opens the group they want.
// (last_occupation was folded into work_situation in Build 5; capture-upstream
// mini-lists + the vertical-toggle layout are unchanged here.)
export const SECTION_GROUPS: Record<string, SectionGroupConfig> = {
  [SECTION_SLUGS.ABOUT_YOU]: {
    groups: [
      {
        id: 'identity',
        title: 'Identity',
        subtitle: 'Who you are on paper.',
        defaultCollapsed: true, // s109 Build 3 Fix 1: land collapsed like every other group
        // Who you are on paper — name/DOB/parents plus marital + spouse fields
        // (merged here from the former "Relationships" group). Spouse fields stay
        // gated by MARITAL_STATUS_SHOW_SPOUSE inside this group.
        fieldKeys: [
          // FIELDS-NAME-1 (s135): name captured as parts. full_legal_name is DERIVED on save and
          // deliberately NOT rendered here (it stays isActive:true for the form field_maps, but is
          // not a user input). first/middle/family/name_at_birth replace it as the visible inputs.
          'first_name',
          'middle_name',
          'family_name',
          // s223 (D-222.8): asked on the interview's `you` screen beside the name. Grouped, or an active
          // field falls into the label-less "ungrouped" render (D-154.9).
          FIELD_KEYS.MOBILE,
          FIELD_KEYS.PHONE_MAKE_MODEL,
          FIELD_KEYS.OTHER_EMAILS,
          'name_at_birth',
          'also_known_as',
          'date_of_birth',
          'place_of_birth',
          'country_of_birth', // s147 FORMS-VALUE-SWEEP-1: beside place_of_birth (Identity)
          'province_of_birth', // s147 Phase 1b: shown when country_of_birth == Canada (showIf)
          'citizenship',
          // s154 round 2 (item 3, A1): organ_donor_status sits its OWN full-width row immediately after
          // Citizenship — it's the planner's status; beside father/mother it read as possibly theirs.
          'organ_donor_status', // FIELD-AUDIT-1 (D-154.5): relocated from Health & Medical
          'organ_donor_registry', // FIELD-AUDIT-1 round 3 (D-154.26): showIf "Registered" companion
          FIELD_KEYS.MARITAL_STATUS,
          'spouse_name',
          'spouse_phone', // s147 FORMS-VALUE-SWEEP-1: beside spouse_name (gated with it)
          'spouse_dob',
          'marriage_date',
          'cohabitation_start_date', // FIELD-AUDIT-1 (D-154.12): showIf Common-Law Partner
          'separation_date',         // FIELD-AUDIT-1 (D-154.12): showIf Separated
          'spouse_sin',        // FIELD-AUDIT-1 (D-154.5): spouseCond, Cat A (benefit apps / final return)
          'father_name',
          'mother_maiden_name',
        ],
      },
      {
        id: 'family',
        title: 'Family',
        subtitle: 'Your people and pets.',
        defaultCollapsed: true,
        // hasDependents is a retired tombstone (isActive:false) — dropped here.
        fieldKeys: [
          FIELD_KEYS.HAS_CHILDREN,
          FIELD_KEYS.HAS_PETS,
        ],
      },
      {
        id: 'your-situation',
        title: 'Home and Work',
        subtitle: 'Where you live and what you do.',
        defaultCollapsed: true,
        fieldKeys: [
          // s155 (Layer-3): the own/rent question renders ABOVE the home address — its notice + helper
          // ride with the field. Presentation order only (card render order = this list); keys unchanged.
          'home_tenure', // s109 Build 4a: own/rent select, seeds the home address downstream
          FIELD_KEYS.HOME_ADDRESS,
          FIELD_KEYS.OWNS_PROPERTY,
          // s111 Build 5: work_situation replaces the owns_business toggle + last_occupation
          // free-text (both retired isActive:false). Self-employed drives owns_business.
          FIELD_KEYS.WORK_SITUATION,
          // FIELD-AUDIT-1 Finding B (D-154.9): owns_business reactivated (s154 #9) but was left out
          // of every group → it fell into the label-less "ungrouped" render (bare switch, no question).
          // Grouped here (beside work_situation, which prefills it) so it renders its question label
          // like the other gate toggles.
          FIELD_KEYS.OWNS_BUSINESS,
          // s154 round 2 (item 3): last_occupation ("Occupation") was reactivated s146 for the
          // 74A/74.1A binding but left out of every group → it leaked into the label-less "ungrouped"
          // render. It's a work fact → grouped here beside work_situation.
          'last_occupation',
          // s119: capture-only firearms toggle — grouped with the other possession toggle
          // (owns_property). Persists to clients.has_firearms via situation-flags; gates no
          // section. On "Yes" it shows a guidance line pointing to Assets → Valuables.
          FIELD_KEYS.HAS_FIREARMS,
          // s171 (R-A): capture-only veterans toggle, grouped beside its sibling capture flag for
          // the same reason — an active About You field that is in no group falls into the
          // label-less "ungrouped" card fallback and renders a bare switch with no question
          // (D-154.9). It is a life fact rather than a possession, but this is the group the other
          // capture-only toggle lives in and splitting them would buy nothing.
          FIELD_KEYS.HAS_VETERANS_SERVICE,
          // s172 Block C: the multiple-wills question. Grouped here on the same reasoning the
          // veterans toggle records above — an ungrouped About You field renders as a bare switch
          // with no question (D-154.9), and this is where the possession/structure toggles live.
          // It sits beside owns_business deliberately: a secondary will most often covers private
          // company shares, so the two questions are read together. 🔴 Placement is the weakest
          // part of this: it is an estate-STRUCTURE fact in a group titled "Home and Work". Worth
          // revisiting at the Planner Magic content review rather than minting a fourth group for
          // one field now.
          'has_multiple_wills',
        ],
      },
    ],
    ungroupedBehavior: 'append',
  },
};

export function getSectionGroups(slug: string): SectionGroupConfig | null {
  return SECTION_GROUPS[slug] ?? null;
}
